// ── Basic in-world chat overlay (DOM). ──
// Sits above the Phaser canvas. Enter focuses the input; sending invokes the callback
// (wired to NetClient.sendChat). Kept intentionally minimal + glassmorphic.

export interface ChatOverlayOptions {
  onSend: (text: string) => void;
  maxMessages?: number;
}

export class ChatOverlay {
  #root: HTMLDivElement;
  #log: HTMLDivElement;
  #input: HTMLInputElement;
  #maxMessages: number;
  /** Mobile only: a 💬 button that expands/collapses the chat so it never covers the joystick. */
  #toggleBtn?: HTMLButtonElement;
  #isTouch: boolean;
  /** Desktop: always visible. Mobile: collapsed to the 💬 icon until tapped. */
  #expanded: boolean;

  constructor(opts: ChatOverlayOptions) {
    this.#maxMessages = opts.maxMessages ?? 50;

    // Anchored bottom-CENTER (not bottom-left) so it never sits on top of the top-left player
    // card or the bottom-left party bar / menu panels. Kept narrow and below the HUD z-order.
    // On touch devices the bottom corners hold the virtual joystick + A/≡ buttons and the iOS
    // home indicator, so lift the chat clear of them (safe-area-aware) and let it use more width.
    const isTouch =
      (typeof window !== 'undefined' &&
        ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
          'ontouchstart' in window)) ||
      false;
    this.#root = document.createElement('div');
    this.#root.style.cssText = [
      'position:fixed',
      'left:50%',
      // clear the home indicator + touch-control band on phones; small inset on desktop
      isTouch ? 'bottom:calc(env(safe-area-inset-bottom,0px) + 96px)' : 'bottom:12px',
      'transform:translateX(-50%)',
      isTouch ? 'width:min(64vw,320px)' : 'width:300px',
      isTouch ? 'max-width:64vw' : 'max-width:46vw',
      'font-family:system-ui,sans-serif',
      'z-index:40',
      'pointer-events:none',
    ].join(';');

    this.#log = document.createElement('div');
    this.#log.style.cssText = [
      'max-height:160px',
      'overflow-y:auto',
      'display:flex',
      'flex-direction:column',
      'gap:2px',
      'margin-bottom:6px',
      'font-size:13px',
      'line-height:1.35',
      'color:#eaeaea',
      'text-shadow:0 1px 2px rgba(0,0,0,0.8)',
    ].join(';');

    this.#input = document.createElement('input');
    this.#input.type = 'text';
    this.#input.placeholder = 'Press Enter to chat…';
    this.#input.maxLength = 200;
    this.#input.style.cssText = [
      'width:100%',
      'box-sizing:border-box',
      'padding:8px 10px',
      'border-radius:10px',
      'border:1px solid rgba(255,255,255,0.15)',
      'background:rgba(20,22,30,0.55)',
      'backdrop-filter:blur(8px)',
      'color:#fff',
      'outline:none',
      'pointer-events:auto',
      'font-size:13px',
    ].join(';');

    this.#input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // keep WASD/Space out of the game while typing
      if (e.key === 'Enter') {
        const text = this.#input.value;
        this.#input.value = '';
        this.#input.blur();
        if (text.trim()) opts.onSend(text);
      } else if (e.key === 'Escape') {
        this.#input.blur();
      }
    });

    this.#isTouch = isTouch;
    this.#expanded = !isTouch;

    this.#input.addEventListener('blur', () => {
      // On phones, blurring (send / dismiss keyboard) collapses chat back to the 💬 icon so it
      // never sits over the joystick / quest panel.
      if (this.#isTouch) this.#collapse();
    });

    this.#root.appendChild(this.#log);
    this.#root.appendChild(this.#input);
    document.body.appendChild(this.#root);

    if (isTouch) {
      // Collapsed by default; a floating 💬 button on the right edge (clear of the top HUD and the
      // bottom joystick/A buttons) toggles the chat open.
      this.#root.style.display = 'none';
      const btn = document.createElement('button');
      btn.textContent = '💬';
      btn.setAttribute('aria-label', 'Chat');
      btn.style.cssText = [
        'position:fixed',
        'top:50%',
        'right:10px',
        'transform:translateY(-50%)',
        'width:46px',
        'height:46px',
        'border-radius:50%',
        'border:1px solid rgba(52,211,153,0.55)',
        'background:rgba(14,16,24,0.82)',
        'backdrop-filter:blur(8px)',
        'color:#fff',
        'font-size:20px',
        'line-height:1',
        'z-index:41',
        'pointer-events:auto',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'box-shadow:0 4px 12px rgba(0,0,0,0.4)',
      ].join(';');
      const onTap = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this.#toggle();
      };
      btn.addEventListener('click', onTap);
      this.#toggleBtn = btn;
      document.body.appendChild(btn);
    }

    // Global Enter focuses chat unless already typing somewhere.
    this.#focusHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && document.activeElement !== this.#input) {
        const tag = (document.activeElement?.tagName ?? '').toLowerCase();
        if (tag !== 'input' && tag !== 'textarea') this.#input.focus();
      }
    };
    window.addEventListener('keydown', this.#focusHandler);
  }

  #focusHandler: (e: KeyboardEvent) => void;

  get isTyping(): boolean {
    return document.activeElement === this.#input;
  }

  #toggle(): void {
    if (this.#expanded) this.#collapse();
    else this.#expand();
  }

  #expand(): void {
    this.#expanded = true;
    this.#root.style.display = '';
    if (this.#toggleBtn) this.#toggleBtn.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)'; // clear unread ring
    // focus on the next frame so the tap that opened it doesn't immediately blur on mobile
    requestAnimationFrame(() => this.#input.focus());
  }

  #collapse(): void {
    if (!this.#isTouch) return;
    this.#expanded = false;
    this.#root.style.display = 'none';
  }

  addMessage(from: string, text: string): void {
    const line = document.createElement('div');
    line.innerHTML = `<b style="color:#7fd1ff">${escapeHtml(from)}</b>: ${escapeHtml(text)}`;
    this.#append(line);
  }

  addSystem(text: string): void {
    const line = document.createElement('div');
    line.style.color = '#b9b9c9';
    line.style.fontStyle = 'italic';
    line.textContent = text;
    this.#append(line);
  }

  /** A received private message — rendered highlighted (gold lock + distinct color) and peeked open. */
  addWhisper(from: string, text: string): void {
    const line = document.createElement('div');
    line.style.color = '#fbbf24'; // gold, distinct from the cyan of public chat
    line.innerHTML = `🔒 <b>[DM] ${escapeHtml(from)}</b>: ${escapeHtml(text)}`;
    this.#append(line);
    // On phones the chat is collapsed by default — briefly peek it open so the DM isn't missed,
    // then re-hide (unless the player expanded it in the meantime). #append already flags the
    // unread ring on the 💬 button so it stays discoverable after the peek closes.
    if (this.#isTouch && !this.#expanded) {
      this.#root.style.display = '';
      window.setTimeout(() => {
        if (this.#isTouch && !this.#expanded) this.#root.style.display = 'none';
      }, 4000);
    }
  }

  #append(line: HTMLDivElement): void {
    this.#log.appendChild(line);
    while (this.#log.childElementCount > this.#maxMessages) {
      this.#log.removeChild(this.#log.firstChild as Node);
    }
    this.#log.scrollTop = this.#log.scrollHeight;
    // Mobile + collapsed → flag unread with a red ring on the 💬 icon.
    if (this.#isTouch && !this.#expanded && this.#toggleBtn) {
      this.#toggleBtn.style.boxShadow = '0 0 0 2px #ff5a5a, 0 4px 12px rgba(0,0,0,0.45)';
    }
  }

  destroy(): void {
    window.removeEventListener('keydown', this.#focusHandler);
    this.#root.remove();
    this.#toggleBtn?.remove();
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
